import { observer } from 'owa-mobx-react';
import type { FolderForestTreeType } from 'owa-graph-schema';
import React from 'react';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import type { RenderFolderNodeFunc } from './MailFolderNodeTreeProps';
import { SEARCH_FOLDER_ROOT_DISTINGUISHED_ID } from 'owa-folders-constants';

interface MailFolderNodeChildrenProps {
    nestDepth: number;
    folderIdsToRender: string[];
    treeType: FolderForestTreeType;
    distinguishedFolderParentIds?: string[];
    isFavoritesSupported?: boolean;
    isRootExpanded?: boolean;
    ignoreSearchFolderParent?: boolean;

    renderFolderNode: RenderFolderNodeFunc;
    onContextMenu?: (
        evt: React.MouseEvent<unknown> | React.TouchEvent<unknown>,
        folderId: string,
        distinguishedFolderParentIds: string[]
    ) => void;
    parentRef?: React.RefObject<HTMLDivElement | null>;
}

/**
 * Component responsible for rendering the nested folder tree
 */
const defaultExport: React.FunctionComponent<MailFolderNodeChildrenProps> = observer(
    function MailFolderNodeChildren(props: MailFolderNodeChildrenProps) {
        const {
            distinguishedFolderParentIds,
            folderIdsToRender,
            isFavoritesSupported,
            isRootExpanded,
            nestDepth,
            renderFolderNode,
            treeType,
            ignoreSearchFolderParent,
            parentRef,
        } = props;

        const onContextMenu = React.useCallback(
            (
                evt: React.MouseEvent<unknown, MouseEvent> | React.TouchEvent<unknown>,
                folderId: string,
                distinguishedFolderParentIdsInner: string[]
            ) => {
                props.onContextMenu?.(evt, folderId, distinguishedFolderParentIdsInner);
            },
            [props.onContextMenu]
        );

        const renderChildren = (
            folderIds: string[],
            currentNestDepth: number
        ): React.JSX.Element[] => {
            const renderedNodes: React.JSX.Element[] = [];
            if (currentNestDepth == 0) {
                if (isRootExpanded) {
                    const renderedChildNodes = renderChildren(folderIds, currentNestDepth + 1);
                    Array.prototype.push.apply(renderedNodes, renderedChildNodes);
                }
            } else {
                folderIds.forEach(folderId => {
                    const folderNode = renderFolderNode(
                        folderId,
                        currentNestDepth,
                        treeType,
                        !!isFavoritesSupported,
                        onContextMenu,
                        distinguishedFolderParentIds,
                        parentRef
                    );

                    const ignoreSearchFolder =
                        ignoreSearchFolderParent &&
                        folderIdToName(folderId) == SEARCH_FOLDER_ROOT_DISTINGUISHED_ID;

                    if (folderNode && !ignoreSearchFolder) {
                        renderedNodes.push(folderNode);
                    }
                });
            }

            return renderedNodes;
        };

        return <>{renderChildren(folderIdsToRender, nestDepth)}</>;
    },
    'MailFolderNodeChildren'
);
export default defaultExport;
